// src/components/blog/BlogThumbnail.tsx
// The cover graphic for a post. The small "card" variant sits on the blog list; the larger "hero"
// variant is the header of the post page (its title is the page's h1).
import React from 'react';
import styled, { css } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BlogPost } from '../../types/blogTypes';
import { estimateReadingMinutes } from '../../utils/blogMarkdown';
import { splitCoverHighlight } from '../../utils/blogFormat';
import { BLOG_ICONS } from './blogIcons';
import logoSrc from '../../assets/images/BlackLogoNoBG.png';

export type ThumbnailVariant = 'card' | 'hero';

interface HeroProps {
  $hero: boolean;
}

/* overflow: clip (not hidden) keeps the rounded corners but lets the aspect-ratio box grow to fit a
   long title instead of cutting off the footer row. container-type lets the card title scale with
   the thumbnail's own width (cqw) rather than the viewport. */
const Frame = styled.div<HeroProps>`
  display: grid;
  grid-template-columns: 1fr 32%;
  aspect-ratio: 768 / 403;
  overflow: clip;
  container-type: inline-size;
  border-radius: 16px;
  border: 1px solid #e3e9f1;
  background: #f8fafd;

  ${({ $hero }) =>
    $hero &&
    css`
      grid-template-columns: 1fr 26%;
      aspect-ratio: auto;
      min-height: 240px;
      border-radius: 28px;
      box-shadow: 0 20px 44px rgba(34, 50, 70, 0.14);

      @media (max-width: 640px) {
        grid-template-columns: 1fr 28%;
        min-height: 0;
        border-radius: 22px;
      }
    `}
`;

const TextPanel = styled.div<HeroProps>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${({ $hero }) => ($hero ? '14px' : '8px')};
  min-width: 0;
  padding: ${({ $hero }) => ($hero ? 'clamp(18px, 3.5%, 30px) clamp(18px, 4%, 36px)' : 'clamp(14px, 6%, 26px)')};
`;

const Tag = styled.span<HeroProps>`
  align-self: flex-start;
  padding: ${({ $hero }) => ($hero ? '5px 13px' : '3px 9px')};
  border-radius: 999px;
  background: #1c3650;
  color: #f7fbff;
  font-size: ${({ $hero }) => ($hero ? '12px' : '10px')};
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const CoverTitle = styled.div<HeroProps>`
  margin: 0;
  font-family: 'Satoshi', 'Inter', system-ui, sans-serif;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: #0f1c2d;

  ${({ $hero }) =>
    $hero
      ? css`
          font-size: clamp(22px, 3.4vw, 38px);
          line-height: 1.12;
        `
      : css`
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 4;
          overflow: hidden;
          font-size: 14px;
          font-size: clamp(12px, 4.6cqw, 19px);
          line-height: 1.2;
        `}
`;

const Highlight = styled.span`
  background: linear-gradient(transparent 62%, rgba(129, 127, 253, 0.38) 62%);
`;

const Footer = styled.div<HeroProps>`
  display: flex;
  align-items: center;
  gap: ${({ $hero }) => ($hero ? '12px' : '8px')};
  font-size: ${({ $hero }) => ($hero ? '14px' : '11px')};
  color: #6b7a8a;
  white-space: nowrap;

  img {
    height: ${({ $hero }) => ($hero ? '22px' : '17px')};
    width: auto;
  }
`;

const IconPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e9eef5;
`;

const IconCircle = styled.div<HeroProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 62%;
  aspect-ratio: 1;
  border-radius: 50%;
  background: rgba(129, 127, 253, 0.16);
  color: #1c3650;
  font-size: ${({ $hero }) => ($hero ? 'clamp(30px, 5vw, 60px)' : 'clamp(24px, 3.2vw, 44px)')};
`;

interface BlogThumbnailProps {
  post: BlogPost;
  variant?: ThumbnailVariant;
}

const BlogThumbnail: React.FC<BlogThumbnailProps> = ({ post, variant = 'card' }) => {
  const isHero = variant === 'hero';
  const { lead, highlight } = splitCoverHighlight(post.title);

  return (
    <Frame
      $hero={isHero}
      aria-hidden={isHero ? undefined : 'true'}
      data-testid={isHero ? 'blog-hero' : 'blog-thumbnail'}
    >
      <TextPanel $hero={isHero}>
        <Tag $hero={isHero}>{post.category}</Tag>
        <CoverTitle $hero={isHero} as={isHero ? 'h1' : 'div'}>
          {lead} <Highlight>{highlight}</Highlight>
        </CoverTitle>
        <Footer $hero={isHero}>
          <img src={logoSrc} alt="" />
          <span>{estimateReadingMinutes(post.body)} min read</span>
        </Footer>
      </TextPanel>
      <IconPanel aria-hidden="true">
        <IconCircle $hero={isHero}>
          <FontAwesomeIcon icon={BLOG_ICONS[post.icon]} />
        </IconCircle>
      </IconPanel>
    </Frame>
  );
};

export default React.memo(BlogThumbnail);
